import {
  usePreloadedQuery,
} from "react-relay"

// REACT_APP_WOZTELL_URL is a "||"-separated whitelist of platform origins.
// When embedded, prefer the entry matching the embedding parent so links and
// API paths stay on the host the user came from; fall back to the first entry.
export function getWoztellUrl() {
  const urls = (process.env.REACT_APP_WOZTELL_URL || "").split("||")
  if (document.referrer) {
    try {
      const referrerOrigin = new URL(document.referrer).origin
      const matched = urls.find((url) => {
        try {
          return new URL(url).origin === referrerOrigin
        } catch (e) {
          return false
        }
      })
      if (matched) {
        return matched
      }
    } catch (e) {
      // malformed referrer: fall through to the first entry
    }
  }
  return urls[0]
}

// Number of trailing dot-separated labels two hostnames share, so that
// account.woztell.com scores 2 against inbox.cn.woztell.com and 0 against
// inbox.woztell.sanuker.com. A suffix comparison avoids needing the public
// suffix list to compute a registrable domain in the browser.
function sharedDomainLabels(a, b) {
  const left = a.split(".").reverse()
  const right = b.split(".").reverse()
  let shared = 0
  while (shared < left.length && shared < right.length && left[shared] === right[shared]) {
    shared += 1
  }
  return shared
}

// REACT_APP_OIDC_ISSUER is a "||"-separated whitelist. The login iframe and the logout
// redirect must both target the issuer that is same-site with this app, otherwise its
// cookies are third-party and Safari and incognito drop them - which shows up as
// "interaction session id cookie not found" on login, and as a logout that destroys an
// empty session on the wrong host.
//
// Matching on our own origin rather than the embedding page's is deliberate and loses
// nothing: a cookie is first-party only when the whole frame chain is same-site, so if
// a platform embeds us cross-site the login iframe is third-party whichever issuer we
// pick. Where the choice can matter, our origin and the top level agree. It also lets
// the API derive the same issuer from the caller Origin header (see resolveOidcIssuer
// in inbox-integration-api) so both ends stay in step without passing it around.
//
// A single value without "||" resolves to itself, so existing deployments are unaffected.
export function getOidcIssuer() {
  const issuers = (process.env.REACT_APP_OIDC_ISSUER || "").split("||").map((s) => s.trim()).filter(Boolean)
  if (issuers.length <= 1) {
    return issuers[0]
  }
  const ownHost = window.location.hostname
  let best = issuers[0]
  let bestScore = -1
  issuers.forEach((issuer) => {
    try {
      const score = sharedDomainLabels(new URL(issuer).hostname, ownHost)
      if (score > bestScore) {
        bestScore = score
        best = issuer
      }
    } catch (e) {
      // malformed entry: skip it
    }
  })
  if (bestScore < 2) {
    console.warn("no OIDC issuer is same-site with", ownHost, "- falling back to", best, "; its cookies will be third-party")
  }
  return best
}

function decodeJwtPayload(jwt) {
  const base64 = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")
  const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0))
  return JSON.parse(new TextDecoder().decode(bytes))
}

function sameOrigin(a, b) {
  try {
    return new URL(a).origin === new URL(b).origin
  } catch (error) {
    return false
  }
}

// /session/end destroys the session behind the session cookie on the host it is called
// on, and cookies are per host. So logout has to go to the issuer the app authenticates
// against, not to whichever issuer minted the stored id_token - that token can be older
// than the current session, and ending the wrong host's session leaves the user logged
// in. id_token_hint is optional, so it is only sent when it was minted by that same
// issuer; otherwise the provider rejects it with "jwt issuer invalid" and client_id
// alone identifies the client.
export async function logoutRequest(idToken, issuer) {
  const { end_session_endpoint } = await fetch(`${issuer}/.well-known/openid-configuration`).then((res) => res.json())
  let idTokenHint
  try {
    const { iss } = decodeJwtPayload(idToken)
    if (sameOrigin(iss, issuer)) {
      idTokenHint = idToken
    } else {
      console.warn("id_token was issued by", iss, "but logging out at", issuer, "- omitting id_token_hint")
    }
  } catch (error) {
    console.warn("could not read iss from id_token, omitting id_token_hint", error)
  }
  return { end_session_endpoint, idTokenHint }
}

export function queryRenderer(Component, {
  queryRef,
  query,
}) {
  return (props) => {
    const data = usePreloadedQuery(
      query,
      queryRef,
    )

    return (
      <Component
        data={data}
        {...props}
      />
    )
  }
}
